import React, { useCallback, useRef, useState, useEffect } from 'react';
import { HLSPlayer, type HLSPlayerHandle } from './components/HLSPlayer';
import { ControlBar } from './components/ControlBar';
import { ViewerCount } from './components/ViewerCount';
import { CustomerLogo } from './components/CustomerLogo';
import { BufferingOverlay } from './components/BufferingOverlay';
import { AudioOnlyOverlay } from './components/AudioOnlyOverlay';
import { BlurOverlay } from './components/BlurOverlay';
import { ForensicWatermark } from './components/ForensicWatermark';
import { ReactionButton } from './components/ReactionButton';
import { useCatchUp } from './hooks/useCatchUp';
import { useReconnection } from './hooks/useReconnection';
import { useFocusDetection } from './hooks/useFocusDetection';
import { useScreenCaptureGuard } from './hooks/useScreenCaptureGuard';
import { useViewerCount } from './hooks/useViewerCount';
import { useReactions } from './hooks/useReactions';
import { useAdBreak } from './hooks/useAdBreak';
import { useSession } from './hooks/useSession';
import { playerConfig } from './config';
import { resolveTheme } from './styles/theme';
import type { PlaybackMode } from './components/ModeIndicator';
import type { QualityLevel, QualityOption } from './components/QualitySelector';

/**
 * PlayerRoot — Main player application component.
 *
 * Orchestrates all player sub-components:
 * - HLSPlayer (hls.js wrapper)
 * - ControlBar (play/pause, volume, fullscreen)
 * - ModeIndicator (LIVE/VOD/AD)
 * - ViewerCount (top-left, live via WebSocket)
 * - CustomerLogo (top-right)
 * - BufferingOverlay (30s reconnection timeout)
 * - Catch-up logic (>3.84s → 1.4x speed)
 * - Dark/light theme with OS preference detection
 * - WebSocket integration (viewer count, reactions, ad breaks)
 */
const App: React.FC = () => {
  const playerRef = useRef<HLSPlayerHandle>(null);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [mode, setMode] = useState<PlaybackMode>('VOD');
  const [showControls, setShowControls] = useState(true);
  const [currentQuality, setCurrentQuality] = useState<QualityLevel>('auto');
  const [isAudioOnly, setIsAudioOnly] = useState(false);

  // Store the mode before switching to RADIO so we can restore it
  const previousModeRef = useRef<PlaybackMode>('VOD');

  const theme = resolveTheme(playerConfig.theme);

  // Session initialization — get signed playback URL and VOD signature
  const { playbackUrl, vodSignatureParams, liveSignatureParams, isLoading: isSessionLoading, error: sessionError } = useSession();

  // Screen capture protection (Req 1.29)
  useScreenCaptureGuard();

  // Focus/visibility detection (Req 1.31, 1.33)
  const isFocusLost = useFocusDetection();

  // Catch-up logic
  useCatchUp(videoElementRef);

  // Reconnection timeout
  const { isTimedOut, remainingSeconds, reset: resetReconnection } =
    useReconnection(isBuffering);

  // WebSocket hooks — independent per service (Req 1.18)
  const { count: viewerCount } = useViewerCount();
  const { sendReaction, incomingReaction, connected: reactionConnected } = useReactions();
  const { adBreakActive } = useAdBreak();

  // Override mode to AD when ad break is active
  useEffect(() => {
    if (adBreakActive) {
      setMode('AD');
    } else {
      // Restore previous mode when ad break ends
      setMode(previousModeRef.current);
    }
  }, [adBreakActive]);

  // Hide controls after inactivity
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    const handleActivity = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setShowControls(false), 3000);
    };

    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('touchstart', handleActivity);

    // Initial show
    timeout = setTimeout(() => setShowControls(false), 3000);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('touchstart', handleActivity);
    };
  }, []);

  // Sync video element ref for catch-up hook
  useEffect(() => {
    const interval = setInterval(() => {
      const el = playerRef.current?.getVideoElement();
      if (el && el !== videoElementRef.current) {
        videoElementRef.current = el;
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const handleTogglePlay = useCallback(() => {
    playerRef.current?.togglePlay();
    setIsPlaying((prev) => !prev);
  }, []);

  const handleVolumeChange = useCallback((newVolume: number) => {
    playerRef.current?.setVolume(newVolume);
    setVolume(newVolume);
    if (newVolume > 0) setIsMuted(false);
  }, []);

  const handleToggleMute = useCallback(() => {
    playerRef.current?.toggleMute();
    setIsMuted((prev) => !prev);
  }, []);

  const handleBuffering = useCallback((buffering: boolean) => {
    setIsBuffering(buffering);
    if (!buffering) {
      setErrorMessage(undefined);
    }
  }, []);

  const handleError = useCallback((error: string) => {
    setErrorMessage(error);
  }, []);

  const handleModeChange = useCallback((newMode: 'LIVE' | 'VOD' | 'AD') => {
    // Don't override RADIO mode from external hls.js events
    if (!isAudioOnly) {
      setMode(newMode);
    }
    // Always update the previous mode ref so we restore correctly
    previousModeRef.current = newMode;
  }, [isAudioOnly]);

  const handleQualityChange = useCallback((option: QualityOption) => {
    setCurrentQuality(option.level);

    if (option.level === 'audio-only') {
      // Enter Audio Only mode (Req 1.36)
      // Save current mode before switching to RADIO
      if (mode !== 'RADIO') {
        previousModeRef.current = mode;
      }
      setIsAudioOnly(true);
      setMode('RADIO');
      // Set hls.js to lowest level to minimize bandwidth
      playerRef.current?.setQualityLevel(4);
    } else {
      // Exit Audio Only mode (Req 1.37) — resume video without interrupting audio
      if (isAudioOnly) {
        setIsAudioOnly(false);
        setMode(previousModeRef.current);
      }
      // Lock hls.js to the selected quality level or re-enable ABR
      playerRef.current?.setQualityLevel(option.hlsLevelIndex);
    }
  }, [mode, isAudioOnly]);

  const handleRetry = useCallback(() => {
    resetReconnection();
    setErrorMessage(undefined);
    setIsBuffering(false);
    window.location.reload();
  }, [resetReconnection]);

  // Show loading state while session is being initialized
  if (isSessionLoading) {
    return (
      <div style={{ ...styles.playerRoot, backgroundColor: theme.colors.background, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#fff', fontSize: '1rem' }}>Initialisation de la session...</span>
      </div>
    );
  }

  // Show session error if no playback URL available
  if (sessionError && !playbackUrl) {
    return (
      <div style={{ ...styles.playerRoot, backgroundColor: theme.colors.background, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#ff6b6b', fontSize: '1rem' }}>{sessionError}</span>
      </div>
    );
  }

  return (
    <div
      style={{
        ...styles.playerRoot,
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.family,
      }}
    >
      {/* Video layer — hidden on focus loss but audio continues (Req 1.33) */}
      <div style={{ ...styles.videoLayer, visibility: (isAudioOnly || isFocusLost) ? 'hidden' : 'visible' }}>
        <HLSPlayer
          ref={playerRef}
          src={playbackUrl || playerConfig.playbackUrl}
          vodSignatureParams={vodSignatureParams}
          liveSignatureParams={liveSignatureParams}
          onBuffering={handleBuffering}
          onError={handleError}
          onModeChange={handleModeChange}
        />
      </div>

      {/* Audio Only overlay (Req 1.36) */}
      <AudioOnlyOverlay logoUrl={playerConfig.logoUrl} visible={isAudioOnly} />

      {/* Blur overlay on focus loss (Req 1.31) */}
      <BlurOverlay active={isFocusLost} logoUrl={playerConfig.logoUrl} />

      {/* Forensic watermark (Req 1.32) */}
      <ForensicWatermark />

      {/* Reaction button (Req 1.16) — hidden during ad breaks (Req 1.25/1.26) */}
      <ReactionButton
        hidden={mode === 'AD'}
        connected={reactionConnected}
        onSendReaction={sendReaction}
        incomingReaction={incomingReaction}
      />

      {/* Overlay elements */}
      <ViewerCount count={viewerCount} />
      <CustomerLogo logoUrl={playerConfig.logoUrl} />

      {/* Buffering / Error overlay */}
      <BufferingOverlay
        isBuffering={isBuffering}
        isTimedOut={isTimedOut}
        remainingSeconds={remainingSeconds}
        errorMessage={errorMessage}
        onRetry={handleRetry}
      />

      {/* Control bar (auto-hide) */}
      <div
        style={{
          ...styles.controlsWrapper,
          opacity: showControls ? 1 : 0,
          pointerEvents: showControls ? 'auto' : 'none',
        }}
      >
        <ControlBar
          isPlaying={isPlaying}
          onTogglePlay={handleTogglePlay}
          onVolumeChange={handleVolumeChange}
          onToggleMute={handleToggleMute}
          isMuted={isMuted}
          volume={volume}
          mode={mode}
          currentQuality={currentQuality}
          onQualityChange={handleQualityChange}
        />
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  playerRoot: {
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    minWidth: '320px',
    maxWidth: '2560px',
    margin: '0 auto',
    userSelect: 'none',
  },
  videoLayer: {
    width: '100%',
    height: '100%',
  },
  controlsWrapper: {
    transition: 'opacity 0.3s ease',
  },
};

export default App;
